import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import api from '@/services/api';
import { useWishlistStore } from '@/store/wishlistStore';
import { useCartStore } from '@/store/cartStore';
import { Product } from '@/types';
import { formatCurrency } from '@/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProductCardSkeleton } from '@/components/common/Skeleton';
import toast from '@/hooks/useToast';

export const WishlistPage = () => {
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null);
  const { setItems: setStoreItems, removeItem: removeStoreItem } = useWishlistStore();
  const { setOpen } = useCartStore();

  const fetchWishlist = () => {
    setLoading(true);
    api.get('/wishlist')
      .then(res => {
        const products = res.data.data.map((w: { product: Product }) => w.product);
        setItems(products);
        setStoreItems(res.data.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchWishlist(); }, []);

  const handleRemove = async (productId: string) => {
    try {
      await api.delete(`/wishlist/${productId}`);
      setItems(prev => prev.filter(p => p.id !== productId));
      removeStoreItem(productId);
      toast.success('Removed from wishlist');
    } catch {
      toast.error('Could not remove item');
    }
  };

  const handleAddToCart = async (product: Product) => {
    if (product.requiresPrescription) {
      toast.error('This product requires a prescription. Upload one on the product page.');
      return;
    }
    setAdding(product.id);
    try {
      await api.post('/cart/items', { productId: product.id, quantity: 1 });
      const cartRes = await api.get('/cart');
      useCartStore.getState().setCart(cartRes.data.data);
      setOpen(true);
      toast.success('Added to cart!');
    } catch {
      toast.error('Could not add to cart');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
          <Heart className="h-5 w-5 text-red-500 fill-red-500" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">My Wishlist</h1>
          {!loading && <p className="text-gray-500 text-sm">{items.length} item{items.length !== 1 ? 's' : ''} saved</p>}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
            <Heart className="h-12 w-12 text-red-200" />
          </div>
          <h3 className="font-heading font-semibold text-xl text-gray-700 mb-2">Your wishlist is empty</h3>
          <p className="text-gray-400 mb-6">Save products you love by clicking the heart icon.</p>
          <Button asChild><Link to="/products">Explore Products</Link></Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(product => (
            <div key={product.id} className="bg-white rounded-card shadow-card overflow-hidden group">
              <div className="relative">
                <Link to={`/products/${product.slug}`}>
                  <img
                    src={product.images[0] || '/placeholder.png'}
                    alt={product.name}
                    className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </Link>
                {product.requiresPrescription && (
                  <Badge variant="rx" className="absolute top-2 left-2 text-xs">Rx</Badge>
                )}
                {product.discountedPrice && product.discountedPrice < product.price && (
                  <Badge variant="accent" className="absolute top-2 right-2 text-xs">Sale</Badge>
                )}
                <button
                  onClick={() => handleRemove(product.id)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove from wishlist"
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </button>
              </div>

              <div className="p-3">
                <Link to={`/products/${product.slug}`}>
                  <h3 className="font-medium text-sm text-gray-800 line-clamp-2 mb-1 hover:text-primary-600">{product.name}</h3>
                </Link>
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="font-bold text-primary-600">{formatCurrency(product.discountedPrice ?? product.price)}</span>
                  {product.discountedPrice && product.discountedPrice < product.price && (
                    <span className="text-xs text-gray-400 line-through">{formatCurrency(product.price)}</span>
                  )}
                </div>

                <Button
                  size="sm"
                  className="w-full"
                  loading={adding === product.id}
                  disabled={product.stock === 0}
                  onClick={() => handleAddToCart(product)}
                >
                  <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
                  {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
